import { useMemo, useState } from "react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { estimateWashPrice, planPrice, ANNUAL_PLANS } from "@/data/site";
import { Check, Flame, CalendarCheck, ShieldCheck, TicketPercent } from "lucide-react";

const INCLUDED = [
  "Sopralluogo aereo con drone",
  "Lavaggio con acqua osmotizzata pura",
  "Nessun ponteggio o scala fissa",
  "Report post-intervento via email",
  "Soddisfatti o ri-lavato gratis",
];

export function Packages({ onBook }) {
  const [kwSel, setKwSel] = useState(6);

  const single = useMemo(() => estimateWashPrice(kwSel), [kwSel]);
  const biennale = useMemo(() => planPrice(kwSel, "biennale"), [kwSel]);
  const fill = ((kwSel - 3) / (60 - 3)) * 100;

  return (
    <section id="pacchetti" className="py-24 sm:py-32" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Piani & Prezzi"
          title={
            <>
              Il tuo tetto pulito, <span className="fire-text">tutto l'anno</span>
            </>
          }
          sub="Piani annuali per i tetti più comuni, oppure stima il prezzo del lavaggio per la potenza esatta del tuo impianto. Paghi online in sicurezza con Stripe, anche a rate, oppure la modalità è da accordare con noi alla conferma."
        />

        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {ANNUAL_PLANS.map((p, i) => {
            const price = planPrice(p.kw, "annuale");
            const singlePrice = estimateWashPrice(p.kw);
            return (
              <Reveal key={p.kw} delay={i * 0.1}>
                <div
                  className={`card-glow relative bg-[#13141B] rounded-3xl p-8 h-full flex flex-col border ${
                    p.popular
                      ? "border-orange-500/70 shadow-[0_0_50px_rgba(255,107,0,0.15)]"
                      : "border-white/10"
                  }`}
                  data-testid={`annual-plan-card-${p.kw}kw`}
                >
                  {p.popular && (
                    <span className="absolute -top-3 left-7 fire-bg text-white font-mono-adj text-[9px] uppercase tracking-[0.2em] font-semibold rounded-full px-3 py-1.5 flex items-center gap-1">
                      <Flame size={11} /> Il Più Scelto
                    </span>
                  )}
                  <p className="font-mono-adj text-[10px] uppercase tracking-[0.25em] text-orange-500">{p.tag}</p>
                  <p className="font-display font-bold text-5xl text-white mt-3">
                    {p.kw} <span className="text-orange-500">kW</span>
                  </p>
                  <p className="font-mono-adj text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">su tetto · Piano Annuale</p>
                  <div className="flex items-end gap-2 mt-5">
                    <p className="font-display font-bold text-5xl fire-text">€{price}</p>
                    <p className="text-slate-500 text-sm mb-1.5">/anno</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    2 lavaggi all'anno · invece di €{singlePrice * 2} <span className="text-emerald-400 font-semibold">risparmi €{singlePrice * 2 - price}</span>
                  </p>
                  <ul className="flex flex-col gap-2.5 mt-6 mb-8">
                    {["2 lavaggi completi con drone", "Promemoria scadenze automatico", "Report resa dopo ogni lavaggio", "Priorità in agenda"].map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                        <Check size={14} className="text-orange-500 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => onBook({ kw: p.kw, plan: "annuale" })}
                    data-testid={`annual-plan-book-button-${p.kw}kw`}
                    className={`mt-auto w-full rounded-full py-3.5 font-mono-adj text-[11px] uppercase tracking-[0.2em] font-semibold transition-all duration-300 ${
                      p.popular
                        ? "fire-bg text-white hover:opacity-90 hover:scale-[1.02]"
                        : "border border-white/15 text-slate-300 hover:border-orange-500/60 hover:text-orange-400"
                    }`}
                  >
                    Attiva Piano Annuale
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8 bg-[#13141B] border border-white/10 rounded-3xl p-7 sm:p-10 grid lg:grid-cols-2 gap-10" data-testid="kw-estimator">
            <div>
              <p className="font-mono-adj text-[10px] uppercase tracking-[0.3em] text-orange-500 font-semibold mb-3">
                Stima il tuo preventivo
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-white leading-tight">
                Quanti kW ha il tuo impianto?
              </h3>
              <div className="flex items-end gap-3 mt-8 mb-6">
                <p className="font-display font-bold text-7xl text-white leading-none" data-testid="estimator-kw-display">{kwSel}</p>
                <p className="font-display font-bold text-3xl fire-text mb-1">kW</p>
              </div>
              <input
                type="range"
                min={3}
                max={60}
                value={kwSel}
                onChange={(e) => setKwSel(Number(e.target.value))}
                className="adj-range w-full"
                style={{ "--fill": `${fill}%` }}
                data-testid="kw-slider-input"
                aria-label="Potenza impianto in kW"
              />
              <div className="flex justify-between font-mono-adj text-[10px] uppercase tracking-[0.2em] text-slate-600 mt-3">
                <span>3 kW</span>
                <span>60 kW</span>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-7">
                {INCLUDED.map((f) => (
                  <span key={f} className="flex items-center gap-2 text-xs text-slate-400">
                    <Check size={12} className="text-orange-500 shrink-0" /> {f}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="bg-[#0D0E13] border border-white/10 rounded-3xl p-7 flex-1" data-testid="estimator-single-card">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-mono-adj text-[10px] uppercase tracking-[0.25em] text-slate-500">Lavaggio Singolo</p>
                  <CalendarCheck size={16} className="text-slate-500" />
                </div>
                <p className="font-display font-bold text-5xl text-white mt-3" data-testid="estimator-single-price">€{single}</p>
                <p className="text-xs text-slate-500 mt-2">Un intervento completo, quando vuoi tu.</p>
                <button
                  onClick={() => onBook({ kw: kwSel, plan: "singolo" })}
                  data-testid="estimator-book-single-button"
                  className="mt-6 w-full rounded-full py-3 font-mono-adj text-[11px] uppercase tracking-[0.2em] font-semibold border border-white/15 text-slate-300 hover:border-orange-500/60 hover:text-orange-400 transition-colors duration-300"
                >
                  Prenota Lavaggio Singolo
                </button>
              </div>

              <div className="bg-[#0D0E13] border border-orange-500/40 rounded-3xl p-7 flex-1 relative overflow-hidden" data-testid="estimator-biennale-card">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,107,0,0.18) 0%, transparent 70%)" }} />
                <div className="flex items-center justify-between gap-3">
                  <p className="font-mono-adj text-[10px] uppercase tracking-[0.25em] text-orange-400">Contratto Manutenzione 24 Mesi</p>
                  <ShieldCheck size={16} className="text-orange-400" />
                </div>
                <div className="flex items-end gap-3 mt-3">
                  <p className="font-display font-bold text-5xl fire-text" data-testid="estimator-biennale-price">€{biennale}</p>
                  <p className="font-mono-adj text-[10px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-1.5">-20%</p>
                </div>
                <p className="text-xs text-slate-500 mt-2">4 lavaggi in 2 anni invece di €{single * 4}. Ci pensiamo noi a programmare tutto.</p>
                <button
                  onClick={() => onBook({ kw: kwSel, plan: "biennale" })}
                  data-testid="estimator-book-biennale-button"
                  className="mt-6 w-full rounded-full py-3 fire-bg text-white font-mono-adj text-[11px] uppercase tracking-[0.2em] font-semibold hover:opacity-90 transition-opacity duration-300"
                >
                  Attiva Contratto 24 Mesi
                </button>
                <p className="text-[11px] text-slate-500 text-center mt-3" data-testid="biennale-klarna-note">
                  Pagamento online anche a rate con Klarna al checkout
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border border-white/10 rounded-3xl px-7 py-6 bg-[#0D0E13]" data-testid="promo-strip">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-2xl fire-bg flex items-center justify-center shrink-0">
                <TicketPercent size={19} className="text-white" />
              </div>
              <div>
                <p className="font-heading font-bold text-white">Hai un codice promo?</p>
                <p className="text-sm text-slate-400">Presenta un amico: con il codice <span className="font-mono-adj text-orange-400 font-semibold">ADJ10</span> avete entrambi il 10% di sconto.</p>
              </div>
            </div>
            <p className="font-mono-adj text-[10px] uppercase tracking-[0.2em] text-slate-500 shrink-0">Si applica in fase di prenotazione</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="text-center font-mono-adj text-[11px] uppercase tracking-[0.25em] text-slate-500 mt-10" data-testid="packages-no-payment-note">
            Pagamento online sicuro con Stripe — rateizzazione disponibile sui contratti
          </p>
        </Reveal>
      </div>
    </section>
  );
}
